import { useUIStore } from '../store/uiStore';

// One Undo for a burst of reversible edits. Each edit hands in a restore
// callback; the app toast offers a single Undo that runs all of them, newest
// first. The group lives as long as the toast: it resets when the toast
// times out, is dismissed (button, Escape), or is replaced by an unrelated
// toast. Module-level state, like deferredCommit.ts: nothing else reads it.
//
// The edits themselves stay optimistic and immediate. Undo is a second edit
// that puts things back, unlike deferredCommit, which holds the write.

/** How long the toast stays up; the same window the old Undo bar used. */
export const UNDO_WINDOW_MS = 5000;

export interface UndoEntry {
  /** Toast text when this is the only entry, e.g. "Deleted Clear brush". */
  message: string;
  /** With `noun`, builds the batch text: "Deleted 2 defenses". */
  verb: string;
  noun: string;
  restore: () => void | Promise<void>;
}

let entries: UndoEntry[] = [];
let toastId: string | null = null;
// True while add() swaps the toast, so the outgoing toast's onClose does not
// wipe the group we are extending.
let swapping = false;

export function groupMessage(list: readonly UndoEntry[]): string {
  if (list.length === 1) return list[0].message;
  const first = list[0];
  const same = list.every((e) => e.verb === first.verb && e.noun === first.noun);
  return same ? `${first.verb} ${list.length} ${first.noun}` : `${list.length} changes`;
}

function reset() {
  if (swapping) return;
  entries = [];
  toastId = null;
}

/** Adds an entry to the open group (or starts one) and shows the toast. */
export function add(entry: UndoEntry): void {
  entries.push(entry);
  swapping = true;
  try {
    toastId = useUIStore.getState().showToast({
      message: groupMessage(entries),
      durationMs: UNDO_WINDOW_MS,
      action: { label: 'Undo', onPress: () => void undoLatest() },
      onClose: reset,
    });
  } finally {
    swapping = false;
  }
}

/** Runs every pending restore, newest first, and closes the toast. Returns
 *  false when nothing is pending (a Cmd-Z handler can then fall through). */
export function undoLatest(): boolean {
  if (entries.length === 0) return false;
  const pending = entries.slice().reverse();
  const id = toastId;
  entries = [];
  toastId = null;
  if (id) useUIStore.getState().dismissToast(id);
  for (const e of pending) {
    try {
      void Promise.resolve(e.restore()).catch(() => undefined);
    } catch {
      // A restore that throws must not strand the ones after it.
    }
  }
  return true;
}

export function pendingCount(): number {
  return entries.length;
}

/** Test hook. */
export function resetUndoGroupForTests(): void {
  entries = [];
  toastId = null;
  swapping = false;
}
